import React, { useCallback, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import AppLayout from "@/layouts/AppLayout";

import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

//import background from "@/assets/contact-bg.jpg";
import blue from "@/assets/blue.svg";
import breadcrump from "@/assets/breadcrump.svg";
import phone from "@/assets/phone_colored.svg";
import mail from "@/assets/mail.svg";


import StyledForBackgroundImage, { OverlayContact } from "./Styles/StyledForBackgroundImage";
import { router, usePage } from "@inertiajs/react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "@/utils/SEO";



type FormData = {
    email: string
    lastname: string
    message: string
    name: string
    phone: string
}

const Contact = () => {

    const [loading, setLoading] = useState(false);
    const { t } = useTranslation(["common", "validation", "glossary"]);
    const { executeRecaptcha } = useGoogleReCaptcha()
    const { locale, data } = usePage().props;
    const props = usePage().props;
    const { image } = usePage().props.page.data

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t("error-name", { ns: 'validation' })),
        lastname: Yup.string().required(t("error-lastname", { ns: 'validation' })),
        email: Yup.string().email(t("error-email", { ns: 'validation' })).required(t("required-email", { ns: 'validation' })),
        phone: Yup.number().required(t("error-phone", { ns: 'validation' })).typeError(t("type-number", { ns: 'validation' })),
        message: Yup.string().required(t("error-message", { ns: 'validation' })),
    })

    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
        resolver: yupResolver(validationSchema)
    })

    const onSubmit = useCallback(
        (data: FormData, e: SubmitEvent) => {

            e.preventDefault();
            if (!executeRecaptcha) {
                console.log("Execute recaptcha not yet available");
                return;
            }
            executeRecaptcha("form").then((gReCaptchaToken: string) => {
                submitForm(gReCaptchaToken, data);
            });
        },
        [executeRecaptcha]
    );

    const submitForm = (gReCaptchaToken: string, data: FormData) => {

        router.post(
            `/${locale}/contact`,
            {
                ...data,
                "g-recaptcha-response": gReCaptchaToken
            },
            {
                preserveState: false,
                onStart: () => {
                    setLoading(true);
                },
                onSuccess: () => {

                },
                onFinish: () => {
                    setLoading(false);
                }
            },
        )

    }

    const ErrorMessage = ({ msg }: { msg: string | undefined }) => {

        return (
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}

                className="text-red-500 absolute top-0 left-2 text-sm"
            >
                {msg}
            </motion.div>
        )

    }

    return (
        <AppLayout>
            <SEO seo={props.page.data.seo} />
            <StyledForBackgroundImage image={image}>
                <OverlayContact />
                <div className="bg-image h-[226px] bg-cover bg-center w-full flex justify-center items-end relative pb-10">
                    <div className="text-white container z-10 flex flex-row items-center justify-between flex-wrap px-2 gap-4">
                        <div>
                            <div className="text-xl lg:text-3xl font-bold mb-4">
                                {t("contact", { ns: "glossary" })}
                            </div>

                            <div className="flex flex-row items-center gap-4">
                                <img src={blue} />
                                <div className="text-[11px]">{t("contact", { ns: "glossary" })}</div>
                            </div>
                        </div>
                    </div>
                </div>


                <div className="flex flex-wrap container m-auto mt-10 mb-40 gap-4 lg:gap-20 px-4">

                    <div className="space-y-6 max-lg:flex-grow">
                        <div className="text-3xl max-lg:text-center font-bold">
                            {t("you_need_help", { ns: "glossary" })}
                        </div>
                        <div className="text-center text-lg font-light">
                            {t("contact_us", { ns: "glossary" })}
                        </div>
                        <div className="flex flex-col justify-around max-lg:items-center py-4 gap-5">

                            {data?.phone && <div className="flex flex-row items-center gap-5">
                                <img className="w-5" src={phone} />
                                <a href={`tel:` + data.phone} className="text-sm">
                                    {data.phone}
                                </a>
                            </div>}

                            {data?.email && <div className="flex flex-row items-center gap-5">
                                <img className="w-5" src={mail} />
                                <a href={`mailto:` + data.email} className="text-sm">
                                    {data.email}
                                </a>
                            </div>}

                            <div className="flex flex-row text-red w-min py-2  rounded-xl gap-4">

                                {props?.social?.map((item, index) => {
                                    if (!item.icon && !item.image) {
                                        return null;
                                    }
                                    return (
                                        <a
                                            key={index}
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:scale-125 transition text-primary"
                                        >
                                            {item.icon ? (
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: item.icon,
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src={item.image}
                                                    loading="lazy"
                                                    alt={item.name}
                                                    className="h-5"
                                                />
                                            )}
                                        </a>
                                    );
                                })}

                            </div>

                        </div>
                    </div>

                    <AnimatePresence>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="placeholder-black flex-grow"
                        >
                            <div className="flex flex-column lg:!flex-row gap-5 mt-6">
                                <div className="flex-grow relative">
                                    <input
                                        type="text"

                                        {...register('name')}

                                        placeholder={t('name', { ns: 'validation' })}
                                        className="px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black"
                                    />
                                    {errors.name && <ErrorMessage msg={errors.name.message} />}
                                </div>
                                <div className="flex-grow relative">
                                    <input
                                        type="text"
                                        {...register('lastname')}
                                        placeholder={t('lastname', { ns: 'validation' })}
                                        className="px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black"
                                    />
                                    {errors.lastname && <ErrorMessage msg={errors.lastname.message} />}
                                </div>
                            </div>
                            <div className="flex flex-column lg:!flex-row gap-5 mt-6">
                                <div className="flex-grow relative">
                                    <input
                                        type="email"
                                        {...register('email')}
                                        placeholder="Email *"
                                        className="px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black"
                                    />
                                    {errors.email && <ErrorMessage msg={errors.email.message} />}
                                </div>
                                <div className="flex-grow relative">
                                    <input
                                        type="text"
                                        {...register('phone')}
                                        placeholder={t('phone', { ns: 'validation' })}
                                        className={`px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black`}
                                    />
                                    {errors.phone && <ErrorMessage msg={errors.phone.message} />}
                                </div>
                            </div>
                            <div className="mt-6 flex-grow relative">
                                <textarea
                                    rows={8}
                                    {...register('message')}
                                    placeholder={t('message', { ns: 'validation' })}
                                    className="px-2 pt-3 w-full rounded-xl border-primary placeholder-black"
                                />
                                {errors.message && <ErrorMessage msg={errors.message.message} />}
                            </div>
                            <div className="flex ">
                                <button
                                    title={t("send", { ns: "glossary" })}
                                    className="bg-primary text-white w-[200px] h-[48px] flex items-center justify-center bold text-lg rounded-md mt-4"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {!loading ? (
                                        <>
                                            <img className="mr-2" /> {t("send", { ns: "glossary" })}
                                        </>
                                    ) : (
                                        <CircularProgress
                                            sx={{ color: "white" }}
                                            size={30}
                                        />
                                    )}
                                </button>
                            </div>
                        </form>
                    </AnimatePresence>

                </div>

            </StyledForBackgroundImage>
        </AppLayout>
    );
};

export default Contact;
